import { api } from "../config/variables";

/**
 * User data interface for CobrowseIO initialization
 */
export interface UserData {
	mobile?: string;
	email?: string;
}

/**
 * CobrowseIO instance interface
 */
interface CobrowseInstance {
	license?: string;
	customData?: {
		userId: string;
		email: string;
	};
	start: (options: { fullDevice: boolean }) => void;
}

/**
 * CobrowseIO client interface
 */
interface CobrowseClient {
	client: () => Promise<CobrowseInstance>;
}

/**
 * Extended Window interface with CobrowseIO
 */
declare global {
	interface Window {
		CobrowseIO?: CobrowseClient;
	}
}

/**
 * CobrowseIO Service
 * Handles initialization and configuration of CobrowseIO for customer support
 *
 * Usage:
 * - Initialize with user data: cobrowseService.init(userData)
 * - User data should contain: { mobile, email }
 * - Service automatically normalizes mobile number to last 10 digits
 */
class CobrowseService {
	private initialized: boolean;
	private licenseKey: string | undefined;

	constructor() {
		this.initialized = false;
		this.licenseKey = (
			api as { cobrowse_license_key?: string }
		).cobrowse_license_key;
	}

	/**
	 * Initialize CobrowseIO with user data
	 * @param userData - User object with mobile and email
	 */
	init(userData: UserData = {}) {
		if (this.initialized) {
			return;
		}

		if (!window.CobrowseIO) {
			console.warn("CobrowseIO script not loaded yet");
			return;
		}

		this.initialized = true;

		const rawMobile = String(userData?.mobile || "").trim();
		const normalizedMobile = rawMobile.slice(-10);
		const email = String(userData?.email || "");

		const cobrowse = window.CobrowseIO;

		if (!cobrowse?.client) {
			console.warn("CobrowseIO.client is not available");
			return;
		}

		cobrowse
			.client()
			.then((instance) => {
				if (instance) {
					if (this.licenseKey) {
						// eslint-disable-next-line no-param-reassign
						instance.license = this.licenseKey;
					}

					// eslint-disable-next-line no-param-reassign
					instance.customData = {
						userId: normalizedMobile,
						email,
					};

					instance.start({ fullDevice: false });
					// eslint-disable-next-line no-console
					console.log("CobrowseIO initialized successfully");
				}
			})
			.catch((error) => {
				console.warn("CobrowseIO.start is not available", error);
			});
	}

	/**
	 * Update user data for an already initialized instance
	 * @param userData - User object with mobile and email
	 */
	updateUserData(userData: UserData = {}) {
		if (!window.CobrowseIO?.client) {
			return;
		}

		const rawMobile = String(userData?.mobile || "").trim();
		const normalizedMobile = rawMobile.slice(-10);
		const email = String(userData?.email || "");

		window.CobrowseIO.client()
			.then((instance) => {
				if (instance) {
					// eslint-disable-next-line no-param-reassign
					instance.customData = {
						userId: normalizedMobile,
						email,
					};
				}
			})
			.catch((error) => {
				console.warn("Failed to update CobrowseIO user data", error);
			});
	}

	/**
	 * Check if CobrowseIO is ready
	 * @returns True if CobrowseIO is initialized and available
	 */
	isReady(): boolean {
		return this.initialized && Boolean(window.CobrowseIO?.client);
	}
}

const cobrowseService = new CobrowseService();

export default cobrowseService;
